import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import TrainingCourseEditor from '../components/TrainingCourseEditor';
import { GraduationCap, Plus, Loader2, Trash2, Edit3, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { company_admin: 'Admins', dispatcher: 'Dispatchers', driver: 'Drivers' };

const Training = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/training/courses`, { withCredentials: true });
      setCourses(r.data);
    } catch { toast.error('Could not load courses'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try {
      await axios.delete(`${API}/training/courses/${c.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const totals = useMemo(() => ({
    total: courses.length,
    published: courses.filter((c) => c.status === 'published').length,
    passed: courses.reduce((s, c) => s + (c.stats?.passed || 0), 0),
  }), [courses]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="training-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#002FA7]" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Training Center
              </h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">Build courses, run quizzes, and issue branded certificates.</p>
          </div>
          <button data-testid="course-new-btn" onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-[#00247D]">
            <Plus className="w-4 h-4" /> New course
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Total courses</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="training-total">{totals.total}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Published</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="training-published">{totals.published}</p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Certifications issued</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1" data-testid="training-passed">{totals.passed}</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : courses.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="training-empty">
              No courses yet. Create your first course to get started.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {courses.map((c) => (
                <li key={c.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`course-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${c.status === 'published' ? 'bg-emerald-100 text-emerald-700' : c.status === 'archived' ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>
                          {c.status}
                        </span>
                        <h3 className="text-sm font-semibold text-slate-900">{c.title}</h3>
                      </div>
                      {c.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>}
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(c.assigned_roles || []).map((r) => ROLE_LABEL[r] || r).join(', ') || 'Nobody'}</span>
                        <span>·</span>
                        <span>{(c.modules || []).length} modules · {(c.quiz || []).length} questions</span>
                        <span>·</span>
                        <span data-testid={`course-stats-${c.id}`}>{c.stats?.passed || 0} passed · {c.stats?.enrolled || 0} enrolled</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditing(c)} data-testid={`edit-course-${c.id}`}
                        className="text-[#002FA7] hover:text-[#00247D]"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(c)} data-testid={`delete-course-${c.id}`}
                        className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {editing !== null && (
          <TrainingCourseEditor course={editing} allCourses={courses}
            onClose={() => setEditing(null)}
            onSaved={fetchAll} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Training;
